import { ContentItem, Section } from '@/common/types/lessonPlan';
import pptxgen from 'pptxgenjs';
import {
  activityDetails,
  ActivityType,
  cleanHtmlContent,
  getActivityScript,
  getFormattedActivity,
} from './utils';

// Type guard to check if a type is an ActivityType
function isActivityType(type: string): type is ActivityType {
  return Object.values(ActivityType).includes(type as ActivityType);
}

export function exportToPpt(sections: Section[]) {
  const pptx = new pptxgen();

  // Define a consistent slide style
  const slideTheme = {
    titleBgColor: '3498db', // Nice blue color for title slides
    titleTextColor: 'FFFFFF', // White text for contrast
    subtitleColor: '333333', // Dark grey for subtitle text
    iconSize: 2.5, // Larger icons
  };

  console.log('sections', sections);

  function extractWordSum(value: string): string {
    const parts = value.split('=');
    return parts[0].trim();
  }

  sections.forEach((section) => {
    if (section.type === 'lessonGuidance') {
      // Create the title slide for lesson guidance
      const titleSlide = pptx.addSlide();

      // Add a background shape for visual appeal
      titleSlide.addShape(pptx.ShapeType.rect, {
        x: 0,
        y: 0,
        w: '100%',
        h: '30%',
        fill: { color: slideTheme.titleBgColor },
      });

      // Add the lesson title with increased prominence
      if (section.title) {
        titleSlide.addText(section.title, {
          x: '5%',
          y: '5%',
          w: '90%',
          h: '20%',
          fontSize: 44,
          bold: true,
          color: slideTheme.titleTextColor,
          align: 'center',
          valign: 'middle',
        });
      }

      // Add level info (first content item) with increased size
      const levelInfo = section.content[0]?.value as string;
      if (levelInfo) {
        titleSlide.addText(levelInfo, {
          x: '10%',
          y: '35%',
          w: '80%',
          h: '20%',
          fontSize: 24,
          bold: true,
          color: slideTheme.subtitleColor,
          align: 'left',
        });
      }

      // Add code/morpheme list (second content item)
      const codeList = section.content[1]?.value as string;
      if (codeList) {
        titleSlide.addText(codeList, {
          x: '10%',
          y: '55%',
          w: '80%',
          h: '30%',
          fontSize: 20,
          color: '666666',
          align: 'left',
        });
      }
    } else if (section.type === 'custom') {
      const cleanDescription = cleanHtmlContent(section.description);
      if (cleanDescription) {
        const customSlide = pptx.addSlide();
        customSlide.addText(cleanDescription, {
          x: '10%',
          y: '10%',
          w: '80%',
          h: '80%',
          fontSize: 14,
          color: '000000',
        });
      }
    } else if (section.type === 'wordSums') {
      // Get the icon for word sums
      const activityIcon = activityDetails[ActivityType.WORD_SUMS].icon;

      // Create a title slide for Word Sums
      const titleSlide = pptx.addSlide();

      // Add a background shape for visual appeal
      titleSlide.addShape(pptx.ShapeType.rect, {
        x: 0,
        y: 0,
        w: '100%',
        h: '30%',
        fill: { color: slideTheme.titleBgColor },
      });

      // Add the Word Sums title with increased prominence
      titleSlide.addText('Word Sums', {
        x: '5%',
        y: '5%',
        w: '90%',
        h: '20%',
        fontSize: 54,
        bold: true,
        color: slideTheme.titleTextColor,
        align: 'center',
        valign: 'middle',
      });

      // Include the icon on the title slide with increased size
      titleSlide.addImage({
        path: activityIcon,
        x: '40%',
        y: '35%',
        w: slideTheme.iconSize,
        h: slideTheme.iconSize,
      });

      // Create content slides for word sums
      section.content.forEach((item: ContentItem) => {
        const contentSlide = pptx.addSlide();

        // Add title
        contentSlide.addText('Word Sums', {
          x: 0.5,
          y: 0.5,
          fontSize: 24,
          bold: true,
        });

        // Add script if available
        if (section.script) {
          contentSlide.addText(cleanHtmlContent(section.script), {
            x: 0.5,
            y: 0.75,
            w: '45%',
            fontSize: 14,
            color: '000000',
            align: 'left',
            valign: 'top',
          });
        }

        // Extract and display only the word sum part (before the equals sign)
        const wordSum = extractWordSum(item.value as string);
        contentSlide.addText(wordSum + ' =', {
          x: '10%',
          y: '40%',
          w: '80%',
          h: '20%',
          fontSize: 80,
          bold: true,
          color: '000000',
          align: 'center',
          valign: 'middle',
        });

        // Include the icon on content slides
        contentSlide.addImage({
          path: activityIcon,
          x: 0.6,
          y: 1.4,
          w: 0.4,
          h: 0.4,
        });
      });
    } else if (isActivityType(section.type)) {
      const activityType = section.type;
      const activityTitle = getFormattedActivity(activityType);
      const activityIcon = activityDetails[activityType].icon;
      const activityScript = section.script
        ? cleanHtmlContent(section.script)
        : getActivityScript(activityType);

      // Create a title slide for the activity
      const titleSlide = pptx.addSlide();

      // Add a background shape for visual appeal
      titleSlide.addShape(pptx.ShapeType.rect, {
        x: 0,
        y: 0,
        w: '100%',
        h: '30%',
        fill: { color: slideTheme.titleBgColor },
      });

      // Activity title centered on the slide with increased prominence
      titleSlide.addText(activityTitle, {
        x: '5%',
        y: '5%',
        w: '90%',
        h: '20%',
        fontSize: 54,
        bold: true,
        color: slideTheme.titleTextColor,
        align: 'center',
        valign: 'middle',
      });

      // Include the icon on the title slide with increased size
      titleSlide.addImage({
        path: activityIcon,
        x: '40%',
        y: '35%',
        w: slideTheme.iconSize,
        h: slideTheme.iconSize,
      });

      if (
        activityType === ActivityType.SOUND_RECOGNITION_DRILL ||
        activityType === ActivityType.ORAL_SEGMENTING_DRILL ||
        activityType === ActivityType.SPELLING_DRILL ||
        activityType === ActivityType.SPELLING_WORD_CHAIN ||
        activityType === ActivityType.SENTENCE_DICTATION ||
        activityType === ActivityType.ORAL_BLENDING_DRILL
      ) {
        // For these activities, create a single slide
        const singleSlide = pptx.addSlide();

        // Activity title with improved styling
        singleSlide.addText(activityTitle, {
          x: 0.5,
          y: 0.5,
          fontSize: 28,
          bold: true,
        });

        // Activity script
        if (activityScript) {
          singleSlide.addText(activityScript, {
            x: 0.5,
            y: 0.75,
            w: '45%',
            fontSize: 14,
            color: '000000',
            align: 'left',
            valign: 'top',
          });
        }

        // Main content or instruction
        singleSlide.addText('(refer to lesson plan)', {
          x: '10%',
          y: '50%',
          w: '80%',
          fontSize: 18,
          color: '666666',
          align: 'center',
        });

        // Include the icon
        singleSlide.addImage({
          path: activityIcon,
          x: 0.6,
          y: 1.4,
          w: 0.4,
          h: 0.4,
        });
      } else {
        // For other activities, create slides for each non-empty content item
        section.content.forEach((item: ContentItem) => {
          const isDecodingWordChain =
            activityType === ActivityType.DECODING_WORD_CHAIN;
          const itemValue = cleanHtmlContent(item.value as string);

          if (itemValue) {
            // For decoding word chains, split by commas and create a slide for each word
            if (isDecodingWordChain) {
              const words = itemValue.split(',').map((word) => word.trim());
              words.forEach((word) => {
                const contentSlide = pptx.addSlide();

                // Activity title
                contentSlide.addText(activityTitle, {
                  x: 0.5,
                  y: 0.5,
                  fontSize: 24,
                  bold: true,
                });

                // Activity script
                if (activityScript) {
                  contentSlide.addText(activityScript, {
                    x: 0.5,
                    y: 0.75,
                    w: '45%',
                    fontSize: 14,
                    color: '000000',
                    align: 'left',
                    valign: 'top',
                  });
                }

                // Single word content
                contentSlide.addText(word, {
                  x: '10%',
                  y: '40%',
                  w: '80%',
                  h: '20%',
                  fontSize: 80,
                  bold: true,
                  color: '000000',
                  align: 'center',
                  valign: 'middle',
                });

                // Include the icon
                contentSlide.addImage({
                  path: activityIcon,
                  x: 0.6,
                  y: 1.4,
                  w: 0.4,
                  h: 0.4,
                });
              });
            } else {
              // Original handling for other activities
              const contentSlide = pptx.addSlide();

              // Activity title
              contentSlide.addText(activityTitle, {
                x: 0.5,
                y: 0.5,
                fontSize: 24,
                bold: true,
              });

              // Activity script
              if (activityScript) {
                contentSlide.addText(activityScript, {
                  x: 0.5,
                  y: 0.75,
                  w: '45%',
                  fontSize: 14,
                  color: '000000',
                  align: 'left',
                  valign: 'top',
                });
              }

              // Main content
              const contentText = itemValue;

              contentSlide.addText(contentText, {
                x: '10%',
                y: '40%',
                w: '80%',
                h: '20%',
                fontSize: 80,
                bold: true,
                color: '000000',
                align: 'center',
                valign: 'middle',
              });

              // Include the icon
              contentSlide.addImage({
                path: activityIcon,
                x: 0.6,
                y: 1.4,
                w: 0.4,
                h: 0.4,
              });
            }
          }
        });
      }
    }
  });

  pptx.writeFile({ fileName: 'LessonPlan.pptx' });
}
